import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Camera, AlertCircle } from 'lucide-react';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ open, onClose, onScan }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  // Aynı barkodu birden fazla kez tetiklememek için ref kullan
  const hasScannedRef = useRef(false);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        // ignore stop errors
      }
      scannerRef.current = null;
      setIsScanning(false);
    }
  }, []);

  const startScanner = useCallback(async () => {
    try {
      setError('');
      setIsScanning(true);
      hasScannedRef.current = false;

      const scanner = new Html5Qrcode('barcode-scanner-container');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 180 }, // Code 128 için daha yatay kutu
          formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
        },
        (decodedText: string) => {
          // Bir kez başarılı okuma yap – gürültüyü engelle
          if (hasScannedRef.current) return;
          hasScannedRef.current = true;
          stopScanner();
          onScan(decodedText.trim());
          onClose();
        },
        (errorMessage: string) => {
          // Gürültülü hata log'u; kullanıcıya göstermiyoruz
          console.debug('Barcode scan error:', errorMessage);
        }
      );
    } catch (err: any) {
      setError(err.message || 'Kamera başlatılamadı');
      setIsScanning(false);
    }
  }, [onClose, onScan, stopScanner]);

  useEffect(() => {
    if (!open) {
      void stopScanner();
      return;
    }

    void startScanner();
    return () => {
      void stopScanner();
    };
  }, [open, startScanner, stopScanner]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative w-full max-w-lg bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/60">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border/70 flex items-center justify-between bg-background-secondary/80 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/10">
              <Camera className="w-5 h-5 text-accent" />
            </div>
            <div className="flex flex-col">
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                Barkod Tara (Code 128)
              </h3>
              <p className="text-xs text-foreground-tertiary">
                Ürünün üzerindeki barkodu kameraya hizalayın.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              void stopScanner();
              onClose();
            }}
            className="p-2 hover:bg-background-tertiary rounded-lg transition-smooth"
          >
            <X className="w-5 h-5 text-foreground-secondary" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {error ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <AlertCircle className="w-12 h-12 text-error" />
              <p className="text-sm text-foreground-secondary text-center max-w-sm">
                {error}
              </p>
              <button onClick={startScanner} className="btn-primary">
                Kamerayı Tekrar Dene
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden bg-black border border-border/60">
                <div
                  id="barcode-scanner-container"
                  className="w-full h-60 md:h-64"
                />
                {isScanning && (
                  <>
                    {/* Yatay Code128 kılavuz kutu */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="border-2 border-accent rounded-lg w-72 md:w-80 h-40" />
                    </div>
                    {/* Kırmızı tarama çizgisi */}
                    <div className="pointer-events-none absolute inset-x-6 top-1/2 -translate-y-1/2">
                      <div className="h-[2px] bg-red-500/80 animate-pulse" />
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between text-[11px] text-foreground-tertiary">
                <span>Kamera açıkken barkodu orta alana hizalayın.</span>
                <span className="font-medium text-accent">
                  Yalnızca Code 128 barkodlar okunur.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-background-tertiary/70 flex items-center justify-between text-xs">
          <span className="text-foreground-tertiary">
            {isScanning ? 'Kamera aktif, barkod bekleniyor…' : 'Kamera durduruldu.'}
          </span>
          {!error && (
            <button
              onClick={startScanner}
              className="text-accent hover:text-accent/80 font-medium transition-colors"
            >
              Yeniden Tara
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
